/**
 * Testimonials — site-wide "Patient Stories" section (used on ~35 pages).
 *
 * The featured quote below is a REAL, verbatim 5-star Google review.
 * If you swap it, swap it for another real one — copied word for word from the
 * Google Business Profile, with the reviewer's first name and last initial.
 * Never an unattributed "Verified Patient" placeholder, never composed copy.
 *
 * Rating and review count come from src/lib/practiceStats.js — the single
 * source of truth. Do not hardcode numbers here.
 */
import React from 'react';
import { GOOGLE_REVIEWS, REVIEW_CATALOG } from '@/lib/reviews';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Quote, Star, ArrowRight, PlayCircle } from 'lucide-react';
import {
  RATING_STARS,
  REVIEW_COUNT_DISPLAY,
  PRACTICE_FACTS,
} from '@/lib/practiceStats';

const trustStats = [
  { value: RATING_STARS, label: 'Google Rating' },
  { value: REVIEW_COUNT_DISPLAY, label: 'Google Reviews' },
  { value: PRACTICE_FACTS.yearsImplantFocused, label: 'Years, Implant-Focused' },
];

// Selected from the central approved collection; no duplicate quote text.
const FEATURED_REVIEW = GOOGLE_REVIEWS.find(r => r.id === 'rudy-f') || GOOGLE_REVIEWS[0];

export default function Testimonials() {
  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 via-white to-amber-50/40 relative overflow-hidden">
      <div className="absolute top-20 right-0 w-96 h-96 bg-amber-100/40 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-100/20 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold uppercase tracking-widest mb-5">
            <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
            Real Patients, Real Results
          </span>
          <h2 className="text-4xl lg:text-5xl font-light text-slate-800 mb-5">
            Patient <span className="font-serif italic text-amber-700">Stories</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Hear from our patients about their experience at Trinity Dental Arts &mdash;
            in their own words.
          </p>

          {/* Trust stats */}
          <div className="flex flex-wrap justify-center gap-8 mt-10">
            {trustStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl font-serif italic text-amber-700">{stat.value}</div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* YouTube Playlist Embed — real patient testimonial videos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-amber-200/60">
            <div className="aspect-video bg-slate-900">
              <iframe
                src="https://www.youtube-nocookie.com/embed/59PC9_p-dhU?list=PLpwGsGNql5xT1pqNdSQcYe44-NBnfB4g3&rel=0&playsinline=1"
                title="Trinity Dental Arts patient testimonial videos"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                loading="lazy"
                className="w-full h-full"
              />
            </div>
          </div>
          <p className="text-center text-sm text-slate-500 mt-4 flex items-center justify-center gap-2">
            <PlayCircle className="w-4 h-4 text-amber-600" />
            Watch real patient testimonials from Trinity, FL
          </p>
        </motion.div>

        {/* Featured review + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <div className="relative bg-white rounded-2xl p-8 lg:p-10 shadow-xl shadow-slate-200/60 border border-amber-100">
            <Quote className="absolute top-6 left-6 w-10 h-10 text-amber-200" />
            <blockquote className="relative text-center text-xl lg:text-2xl font-light text-slate-700 leading-relaxed italic pt-6">
              &ldquo;{FEATURED_REVIEW?.text}&rdquo;
            </blockquote>
            <div className="flex items-center justify-center gap-1 mt-6">
              {[...Array(FEATURED_REVIEW?.rating || 0)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
              ))}
            </div>
            <div className="text-center mt-3 text-sm font-medium text-slate-600">
              &mdash; {FEATURED_REVIEW?.name}
              <span className="text-slate-400"> &middot; Google review</span>
            </div>
            <div className="text-center mt-2">
              <a
                href={REVIEW_CATALOG.pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-slate-400 hover:text-amber-700 underline"
              >
                Selected testimonial · Review selection and Google rating details
              </a>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/our-results"
                className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-7 py-3 rounded-full transition-colors"
              >
                See More Patient Stories
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/book-appointment#signature-consult"
                className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 font-semibold px-4 py-3 transition-colors"
              >
                Start Your Story
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
